import axios from 'axios';
import { HOST_API_KEY, USER_GLOBAL_HOST_API_KEY } from '../config-global';
import { showError } from './snackbar';

const isRateLimitError = (error) =>
  error?.response?.status === 429 ||
  error?.response?.data?.message === 'Too many requests, please try again later.';

const axiosInstance = axios.create({
  baseURL: HOST_API_KEY,
  withCredentials: true,
  crossDomain: true,
});

const shouldAttemptSessionRefresh = (url = '') => {
  const normalizedUrl = url.toLowerCase();
  return !normalizedUrl.includes('/auth/') && !normalizedUrl.includes('/no-auth/');
};

const resolveAuthPartnerId = () => {
  const currentPartnerRaw = localStorage.getItem('currentPartner');
  if (
    currentPartnerRaw &&
    currentPartnerRaw !== 'undefined' &&
    currentPartnerRaw !== 'null'
  ) {
    try {
      const currentPartner = JSON.parse(currentPartnerRaw);
      if (currentPartner?._id) {
        return currentPartner._id;
      }
    } catch {
      // ignore malformed currentPartner
    }
  }

  const manageId = localStorage.getItem('partnerIdmanage');
  if (manageId) {
    return manageId;
  }

  return '';
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.skipPartnerHeader) {
      return config;
    }

    const partnerId = config.partnerContextId || resolveAuthPartnerId();

    if (partnerId) {
      config.headers['x-partner-id'] = partnerId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let refreshPromise = null;

const logoutOnSessionExpiry = () => {
  localStorage.clear();
  window.location.href = '/login';
};

const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${USER_GLOBAL_HOST_API_KEY}/auth/refresh`, {}, { withCredentials: true })
      .catch((refreshError) => {
        const refreshStatus = refreshError.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          logoutOnSessionExpiry();
        }
        throw refreshError;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      shouldAttemptSessionRefresh(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        await refreshSession();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (isRateLimitError(error)) {
      showError('Too many requests, please try again later.');
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;
